import useSWR from "swr";
import axios from "axios";
import Link from "next/link";
import Head from "next/head";
import Image from "next/image";
import Pagination from "@/components/Pagination";
import Table from "@/components/Table";
import TeamfinderFilterModal from "@/components/Modal/TeamfinderFilterModal";
import { GameModes } from "@/models/game.model";
import { Role, Rank } from "@/models/user.model";
import { UserLayout } from "@/components/Layout";
import { Teamfinders, TeamfinderSummoner } from "@/models/teamfinder.model";
import { useTranslation } from "next-i18next";
import { NextPageWithLayout } from "@/pages/_app";
import { useEffect, useState } from "react";
import { ROLES } from "@/constants";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { SummonerRoles } from "@/models/competition.model";
import { FilterIcon } from "@/icons";

export type TeamfinderFilters = {
  ranks?: Rank[];
  roles?: Role[];
  modes?: GameModes[];
  orderBy?: "lastUpdated" | "summonername";
  order?: "ASC" | " DESC";
  school?: string;
  location?: string;
  distance?: number;
};

const TeamfinderPage: NextPageWithLayout = () => {
  const [teamfinderAgents, setTeamfinderAgents] = useState({} as Teamfinders);
  const [activeFilters, setActiveFilters] = useState({} as TeamfinderFilters);
  const [teamfinderFilterModal, setTeamfinderFilterModal] = useState(false);

  const { data } = useSWR(`/api/teamfinders`);
  const { t } = useTranslation(["common", "pageTitle"]);

  useEffect(() => {
    if (!data) return;

    setTeamfinderAgents(data);
  }, [data]);

  // Handle teamfinder filters
  const fetchFilteredAgents = (filters: TeamfinderFilters) => {
    axios
      .post(`/api/teamfinders`, filters)
      .then((res) => setTeamfinderAgents(res.data));
  };

  const handleRankFilter = (rankFilters: Rank[]) => {
    setActiveFilters({ ...activeFilters, ...rankFilters });
    fetchFilteredAgents({ ...activeFilters, ...rankFilters });
  };

  const handleRoleFilter = (roleFilters: Role[]) => {
    setActiveFilters({ ...activeFilters, ...roleFilters });
    fetchFilteredAgents({ ...activeFilters, ...roleFilters });
  };

  const handleModeFilter = (modeFilters: GameModes[]) => {
    setActiveFilters({ ...activeFilters, ...modeFilters });
    fetchFilteredAgents({ ...activeFilters, ...modeFilters });
  };

  return (
    <>
      <Head>
        <title>{`${t("teamfinderPage", { ns: "pageTitle" })} ${t("baseTitle", {
          ns: "pageTitle",
        })}`}</title>
      </Head>

      <div className="mb-8 flex items-center justify-between">
        <div className="space-y-2">
          <h2 className="text-4xl font-bold">Teamfinder</h2>
          <p>Find your next teammates and start competing!</p>
        </div>

        <button
          className="flex items-center"
          onClick={() => setTeamfinderFilterModal(true)}
        >
          filter <FilterIcon className="mx-2 mt-1" />
        </button>
      </div>

      <Table>
        <Table.TableHeaderRow className="grid-cols-[3fr_160px_4fr_150px_32px]">
          <Table.TableHeader>Summoner</Table.TableHeader>
          <Table.TableHeader>Roles</Table.TableHeader>
          <Table.TableHeader>Game modes</Table.TableHeader>
          <Table.TableHeader>Last updated</Table.TableHeader>
        </Table.TableHeaderRow>

        <div>
          {teamfinderAgents.items?.map((agent) => (
            <Table.TableBodyRow
              key={agent.summoner.name}
              className="grid-cols-[3fr_160px_4fr_150px_32px]"
              dropdown
              dropdownContent={<AgentDetails agent={agent} />}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Image
                    src={`/images/emblems/${agent.summoner.rank.tier}_Emblem.webp`}
                    height={40}
                    width={40}
                    alt=""
                  />
                  <div className="ml-3">
                    <Link href={`/summoner/${agent.summoner.name}`}>
                      <a className="w-fit text-sm font-medium group-hover:text-link">
                        {agent.summoner.name}
                      </a>
                    </Link>
                    <p className="text-sm text-slate-500">
                      {`${agent.summoner.rank.tier} `}{" "}
                      {agent.summoner.rank.division}
                    </p>
                  </div>
                </div>
                <Image
                  src="/images/opgg.webp"
                  height={12}
                  width={40}
                  className="cursor-pointer opacity-30 group-hover:opacity-100"
                  alt="OP.GG"
                />
              </div>
              <div className="flex flex-nowrap gap-2 lg:flex-wrap">
                {ROLES.map((role, index) => (
                  <div
                    key={index}
                    className={`flex items-center ${
                      agent.role.includes(role.toUpperCase() as SummonerRoles)
                        ? `opacity-100`
                        : `opacity-10`
                    }`}
                  >
                    <Image
                      src={`/images/role_icons/${role}_icon.png`}
                      height={20}
                      width={20}
                      alt=""
                    />
                  </div>
                ))}
              </div>
              <div className="flex flex-nowrap items-center lg:flex-wrap">
                {agent.queues.map((mode, index) => (
                  <span
                    key={index}
                    className="my-1 mr-2 inline-flex rounded-md bg-slate-800 px-2 py-1 text-xs font-semibold leading-5 text-slate-400"
                  >
                    {mode}
                  </span>
                ))}
              </div>
              <div className="text-slate-500">
                <p className="text-sm">{agent.updatedAt}</p>
              </div>
            </Table.TableBodyRow>
          ))}
        </div>
      </Table>

      <Pagination
        setState={setTeamfinderAgents}
        pagination={teamfinderAgents.pagination}
      />
      <TeamfinderFilterModal
        isModalOpen={teamfinderFilterModal}
        setIsModalOpen={setTeamfinderFilterModal}
        setTeamfinderAgents={setTeamfinderAgents}
      />
    </>
  );
};

const AgentDetails = ({ agent }: { agent: TeamfinderSummoner }) => {
  return (
    <Table.TableDropdown>
      <dl className="ml-12 pt-2 text-slate-500">
        <div className="grid grid-cols-[100px_1fr] space-x-4 text-sm">
          <dd>Location</dd>
          <dt>{agent?.location ?? "N/A"}</dt>
        </div>
        <div className="grid grid-cols-[100px_1fr] space-x-4 text-sm">
          <dd>School</dd>
          <dt>{agent?.school ?? "N/A"}</dt>
        </div>
      </dl>
    </Table.TableDropdown>
  );
};

export async function getStaticProps(props: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(props.locale, ["common", "pageTitle"])),
    },
  };
}

TeamfinderPage.getLayout = UserLayout;

export default TeamfinderPage;
